import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? <div className="route-link-btn-row">{card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}</div> : null}
      </article>
    ))}
  </div>
);

const faqItems = [
  {
    question: "How long does a taxi from Heathrow to Central London take?",
    answer: "Most journeys take around 40 to 90 minutes. The terminal, London drop-off, time of day and live traffic conditions can all affect the journey time.",
  },
  {
    question: "Can you collect from every Heathrow terminal?",
    answer: "Yes. Pickups are available from Heathrow terminals 2, 3, 4 and 5. Add your flight or terminal details so the correct pickup can be arranged.",
  },
  {
    question: "Do you monitor arriving flights?",
    answer: "Yes. Heathrow arrivals can be monitored so the pickup can account for landing delays and the time needed to clear the terminal.",
  },
  {
    question: "Is the Heathrow to London fare fixed?",
    answer: "Your confirmed fare is agreed before travel, with no taxi meter or surge pricing. The quote depends on the exact London address, vehicle and journey details.",
  },
];

const heathrowToLondonRoute = {
  id: "heathrow-to-london",
  path: "/heathrow-to-london",
  liveStripVariant: "london",
  seo: {
    title: "Heathrow to London Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description: "Book a fixed-price taxi from Heathrow Airport to Central London. Licensed drivers, flight monitoring, meet-and-greet support and secure online booking.",
    keywords: "Heathrow to London taxi, Heathrow Airport to Central London transfer, taxi from Heathrow to London, Heathrow airport pickup, fixed Heathrow taxi fare, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/heathrow-to-london",
    ogTitle: "Heathrow to London Taxi - Fixed-Price Airport Transfers",
    ogDescription: "Pre-book a reliable Heathrow to London taxi with a fixed fare, licensed driver and flight monitoring.",
    serviceName: "CheapCabGrab - Heathrow to London Taxi",
    routeName: "Heathrow to London Taxi",
    areaServed: ["Heathrow Airport", "Central London", "West London", "City of London", "Greater London"],
    offerCatalogName: "Heathrow to London Taxi Routes",
    offerName: "Heathrow Airport to London fixed-price taxi",
    offerDescription: "Fixed-price private taxi transfers from Heathrow terminals to Central and Greater London addresses.",
    reservationName: "Taxi booking from Heathrow to London",
  },
  hero: {
    eyebrow: "Heathrow to London taxi route",
    h1: "Heathrow to London Taxi - Fixed-Price Airport Transfers",
    subtitle: "Heathrow terminals 2, 3, 4 and 5 to Central London hotels, homes, stations and business addresses.",
    body: "Flying to the UK? Pre-book your private Heathrow pickup before you travel. Get a clear quote with licensed drivers, flight monitoring and secure online booking.",
  },
  quote: {
    label: "Get your Heathrow to London quote",
    note: "Free - Fast - No obligation",
    presetPickup: "Heathrow Airport",
    presetDropoff: "",
    pickupPlaceholder: "Heathrow Airport or terminal",
    dropoffPlaceholder: "London postcode, hotel or address",
  },
  quotePromises: ["Fixed upfront fare", "Flight monitoring", "Licensed drivers", "Secure online booking"],
  flags: [
    "All Heathrow terminals covered",
    "Central and Greater London drop-offs",
    "Fixed fares agreed before travel",
    "Meet-and-greet support available",
  ],
  reviewPageKey: "/heathrow-to-london",
  reviewAriaLabel: "Heathrow to London customer reviews",
  lhrStatusKicker: "Live Heathrow + London intelligence",
  lhrStatusTitle: "Live checks for your Heathrow to London taxi",
  statusIntro: "Live Heathrow, road, rail and weather signals are checked to support your airport journey planning.",
  floatingCta: "Book Heathrow to London Taxi",
  mobileCta: "Get Heathrow to London Quote",
  supportNudge: "Need Heathrow to London help?",
  faqItems,
  sections: [
    {
      id: "route-overview",
      title: "Heathrow to London at a glance",
      body: "A busy airport corridor for international arrivals, London hotel stays, families, business travellers and residents returning home.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: commonly around 18-32 miles, depending on the London address",
            "Typical time: around 40-90 minutes, depending on traffic",
            "Pickup: Heathrow terminals 2, 3, 4 and 5",
            "Drop-offs: Central London, West London, the City and Greater London",
          ],
        },
        {
          title: "Ideal for",
          items: [
            "International visitors booking before they fly",
            "Families travelling with children and luggage",
            "Hotel, home, station and business-address transfers",
            "Saloon and MPV journeys with door-to-door service",
          ],
        },
        {
          title: "Included in your journey",
          items: [
            "Fare confirmed before travel",
            "Professional licensed driver",
            "Flight monitoring for Heathrow arrivals",
            "Booking support before and after pickup",
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Heathrow arrival",
      body: "Confirm your terminal, check live flight information and compare London travel options before booking.",
      render: () => cardGrid("route-links-grid", [
        { title: "Heathrow Terminal Finder", body: "Check which Heathrow terminal your airline uses before arranging your pickup.", link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" } },
        { title: "Heathrow Flight Checker", body: "Review current Heathrow arrival and departure information before you travel.", link: { to: "/flight-checker", label: "Check Heathrow flights", className: "route-link-btn" } },
        {
          title: "Travelling as a group?",
          body: "For eight or more passengers, use the dedicated Heathrow to London minibus or coach pages.",
          links: [
            { to: "/heathrow-to-london-minibus", label: "Minibus options" },
            { to: "/heathrow-to-london-coach", label: "Coach options", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-areas",
      title: "Popular Heathrow to London destinations",
      body: "Enter the full postcode, hotel or address for the most accurate route and fare.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Central London",
          items: ["Westminster, Victoria and the West End", "Paddington, Marylebone and King's Cross", "Mayfair, Kensington and Chelsea", "City of London and Canary Wharf"],
          link: { to: "/london", label: "London travel guide ->" },
        },
        {
          title: "Heathrow pickup planning",
          items: ["Terminal-specific pickup instructions", "Flight details checked before dispatch", "Meet-and-greet support on request", "Space for luggage matched to the selected vehicle"],
          link: { to: "/heathrow-taxi", label: "Heathrow taxi guide ->" },
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Heathrow to London taxi FAQs",
      body: "Common questions from passengers arranging a private Heathrow Airport transfer.",
      render: () => (
        <div className="route-faq-grid">
          {faqItems.map((item) => <article className="route-faq-card" key={item.question}><h3>{item.question}</h3><p>{item.answer}</p></article>)}
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Heathrow to London booking?",
      body: "Contact support for child seats, extra luggage, multiple stops, accessibility needs or a group arrival.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            Related pages: <Link to="/heathrow-taxi">Heathrow taxi</Link>,{" "}
            <Link to="/train-vs-taxi-heathrow">Heathrow train vs taxi</Link>,{" "}
            <Link to="/central-london-heathrow-parking-vs-taxi">London parking vs taxi</Link> and{" "}
            <Link to="/faq">frequently asked questions</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const HeathrowToLondon = () => <CcgRoutePremiumPage route={heathrowToLondonRoute} QuoteComponent={PostcodeQuotenew} />;

export default HeathrowToLondon;
